import { useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import toast from 'react-hot-toast';
import {
  rendicionAnualService,
  ModalidadAporte,
  TipoPersonaAportante,
} from '../../../services/api';
import type { AportePrivado, AportesTotales } from './types';

const aporteSchema = z
  .object({
    apellido: z.string().trim().min(2, 'Requerido'),
    nombre: z.string().optional(),
    razonSocial: z.string().optional(),
    dni: z.string().optional(),
    cuit: z.string().regex(/^\d{2}-?\d{8}-?\d$/, 'CUIT inválido (ej: 20-12345678-9)'),
    tipoPersona: z.enum(['HUMANA', 'JURIDICA']),
    modalidad: z.enum(['TRANSFERENCIA', 'DEPOSITO', 'ESPECIE']),
    descripcionEspecie: z.string().optional(),
    fecha: z.string().min(1, 'Requerida'),
    importe: z.coerce.number().positive('Debe ser mayor a 0'),
  })
  .superRefine((d, ctx) => {
    if (d.tipoPersona === 'HUMANA') {
      if (!d.nombre) ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['nombre'], message: 'Requerido' });
      if (!d.dni) ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['dni'], message: 'Requerido' });
    } else if (!d.razonSocial) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['razonSocial'], message: 'Requerida' });
    }
    if (d.modalidad === 'ESPECIE' && (!d.descripcionEspecie || d.descripcionEspecie.length < 3)) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['descripcionEspecie'], message: 'Requerido para aporte en especie' });
    }
  });

type AporteForm = z.infer<typeof aporteSchema>;

interface Props {
  rendicionId: string;
  readonly: boolean;
  /** Si true, los datos vienen del snapshot inyectado en cache; no refetch del server. */
  snapshotMode?: boolean;
  onBack: () => void;
  onNext: () => void;
}

function formatImporte(n: number | string | null | undefined): string {
  const v = Number(n ?? 0);
  return v.toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function formatFecha(f: string): string {
  return new Date(f).toLocaleDateString('es-AR', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

export default function Paso2Aportes({ rendicionId, readonly, snapshotMode, onBack, onNext }: Props) {
  const queryClient = useQueryClient();
  const [tab, setTab] = useState<'TRANSFERENCIA' | 'ESPECIE'>('TRANSFERENCIA');
  const [showModal, setShowModal] = useState(false);
  const [editando, setEditando] = useState<AportePrivado | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['rendicion-aportes', rendicionId],
    queryFn: async () => {
      const res = await rendicionAnualService.listAportes(rendicionId);
      return res.data as { items: AportePrivado[]; totales: AportesTotales };
    },
    enabled: !snapshotMode,
    staleTime: snapshotMode ? Infinity : 0,
  });

  const items = data?.items ?? [];
  const totales = data?.totales;

  const aportesFiltrados = useMemo(() => {
    if (tab === 'ESPECIE') return items.filter((a) => a.modalidad === 'ESPECIE');
    return items.filter((a) => a.modalidad !== 'ESPECIE');
  }, [items, tab]);

  const {
    register,
    handleSubmit,
    reset,
    watch,
    formState: { errors },
  } = useForm<AporteForm>({
    resolver: zodResolver(aporteSchema),
    defaultValues: {
      apellido: '',
      nombre: '',
      razonSocial: '',
      dni: '',
      cuit: '',
      tipoPersona: 'HUMANA',
      modalidad: tab === 'ESPECIE' ? 'ESPECIE' : 'TRANSFERENCIA',
      descripcionEspecie: '',
      fecha: new Date().toISOString().slice(0, 10),
      importe: undefined as any,
    },
  });

  const tipoPersonaActual = watch('tipoPersona');
  const modalidadActual = watch('modalidad');

  const saveMutation = useMutation({
    mutationFn: async (form: AporteForm) => {
      const payload = {
        apellido: form.apellido,
        nombre: form.nombre || null,
        razonSocial: form.razonSocial || null,
        dni: form.dni || null,
        cuit: form.cuit,
        tipoPersona: form.tipoPersona as TipoPersonaAportante,
        modalidad: form.modalidad as ModalidadAporte,
        descripcionEspecie: form.descripcionEspecie || null,
        fecha: form.fecha,
        importe: form.importe,
      };
      if (editando) {
        return rendicionAnualService.updateAporte(rendicionId, editando.id, payload);
      }
      return rendicionAnualService.createAporte(rendicionId, payload);
    },
    onSuccess: () => {
      toast.success(editando ? 'Aporte actualizado' : 'Aporte registrado');
      queryClient.invalidateQueries({ queryKey: ['rendicion-aportes', rendicionId] });
      queryClient.invalidateQueries({ queryKey: ['rendicion-saldos', rendicionId] });
      setShowModal(false);
      setEditando(null);
      reset();
    },
    onError: (err: any) => toast.error(err?.response?.data?.error?.message ?? 'No se pudo guardar'),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => rendicionAnualService.deleteAporte(rendicionId, id),
    onSuccess: () => {
      toast.success('Aporte eliminado');
      queryClient.invalidateQueries({ queryKey: ['rendicion-aportes', rendicionId] });
      queryClient.invalidateQueries({ queryKey: ['rendicion-saldos', rendicionId] });
    },
    onError: (err: any) => toast.error(err?.response?.data?.error?.message ?? 'No se pudo eliminar'),
  });

  function openCreate() {
    setEditando(null);
    reset({
      apellido: '',
      nombre: '',
      razonSocial: '',
      dni: '',
      cuit: '',
      tipoPersona: 'HUMANA',
      modalidad: tab === 'ESPECIE' ? 'ESPECIE' : 'TRANSFERENCIA',
      descripcionEspecie: '',
      fecha: new Date().toISOString().slice(0, 10),
      importe: undefined as any,
    });
    setShowModal(true);
  }

  function openEdit(a: AportePrivado) {
    setEditando(a);
    reset({
      apellido: a.apellido,
      nombre: a.nombre ?? '',
      razonSocial: a.razonSocial ?? '',
      dni: a.dni ?? '',
      cuit: a.cuit,
      tipoPersona: a.tipoPersona,
      modalidad: a.modalidad,
      descripcionEspecie: a.descripcionEspecie ?? '',
      fecha: a.fecha.slice(0, 10),
      importe: Number(a.importe),
    });
    setShowModal(true);
  }

  function closeModal() {
    if (saveMutation.isPending) return;
    setShowModal(false);
    setEditando(null);
  }

  if (isLoading) {
    return <div className="bg-white rounded-lg shadow p-8 text-center text-sm text-gray-500">Cargando...</div>;
  }

  return (
    <div className="space-y-4">
      <div className="bg-white rounded-lg shadow p-6">
        <h2 className="text-lg font-semibold text-gray-900 border-b border-gray-200 pb-3 mb-4">
          Aportes Privados — Detalle individualizado
        </h2>

        <div className="bg-amber-50 border-l-4 border-amber-400 p-3 text-sm text-amber-900 mb-4">
          <strong>Art. 50 Ley XII-24:</strong> todo aporte debe identificarse con DNI/CUIT del aportante.
          Aportes anónimos prohibidos. Los totales se trasladan automáticamente a las cuentas 4.2.x del Paso 3.
        </div>

        <div className="flex gap-1 bg-gray-100 rounded-lg p-1 mb-4">
          <button
            type="button"
            onClick={() => setTab('TRANSFERENCIA')}
            className={`flex-1 px-4 py-2 text-sm font-medium rounded-lg transition ${
              tab === 'TRANSFERENCIA' ? 'bg-white text-gray-900 shadow' : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            Por Transferencia / Depósito
          </button>
          <button
            type="button"
            onClick={() => setTab('ESPECIE')}
            className={`flex-1 px-4 py-2 text-sm font-medium rounded-lg transition ${
              tab === 'ESPECIE' ? 'bg-white text-gray-900 shadow' : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            En Especie
          </button>
        </div>

        <div className="flex justify-between items-center mb-3">
          <p className="text-sm text-gray-600">
            {tab === 'TRANSFERENCIA'
              ? 'Aportes recibidos por transferencia o depósito en cuenta única partidaria.'
              : 'Aportes no monetarios: bienes, servicios o gastos pagados por terceros.'}
          </p>
          {!readonly && (
            <button
              type="button"
              onClick={openCreate}
              className="bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium px-4 py-2 rounded-lg"
            >
              + Agregar aporte
            </button>
          )}
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Aportante</th>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">DNI / CUIT</th>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Tipo</th>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                  {tab === 'ESPECIE' ? 'Descripción' : 'Modalidad'}
                </th>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Fecha</th>
                <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">Importe</th>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Alertas</th>
                {!readonly && <th className="px-3 py-2"></th>}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 bg-white">
              {aportesFiltrados.length === 0 ? (
                <tr>
                  <td colSpan={readonly ? 7 : 8} className="px-3 py-6 text-center text-sm text-gray-500">
                    Sin aportes cargados.
                  </td>
                </tr>
              ) : (
                aportesFiltrados.map((a) => (
                  <tr key={a.id}>
                    <td className="px-3 py-2 text-gray-900">
                      {a.tipoPersona === 'HUMANA'
                        ? `${a.apellido}, ${a.nombre ?? ''}`
                        : a.razonSocial ?? a.apellido}
                    </td>
                    <td className="px-3 py-2 font-mono text-xs text-gray-700">
                      {a.dni && <div>DNI {a.dni}</div>}
                      <div>{a.cuit}</div>
                    </td>
                    <td className="px-3 py-2 text-gray-700">
                      {a.tipoPersona === 'HUMANA' ? 'Humana' : 'Jurídica'}
                    </td>
                    <td className="px-3 py-2 text-gray-700">
                      {tab === 'ESPECIE' ? a.descripcionEspecie : a.modalidad}
                    </td>
                    <td className="px-3 py-2 text-gray-700">{formatFecha(a.fecha)}</td>
                    <td className="px-3 py-2 text-right font-mono text-gray-900">
                      {formatImporte(a.importe)}
                    </td>
                    <td className="px-3 py-2">
                      {a.alertaProveedorEstado ? (
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800">
                          Prov. Estado
                        </span>
                      ) : (
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                          OK
                        </span>
                      )}
                    </td>
                    {!readonly && (
                      <td className="px-3 py-2 text-right whitespace-nowrap">
                        <button
                          type="button"
                          onClick={() => openEdit(a)}
                          className="text-blue-600 hover:text-blue-800 text-xs mr-2"
                        >
                          Editar
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            if (confirm('¿Eliminar este aporte?')) deleteMutation.mutate(a.id);
                          }}
                          className="text-red-600 hover:text-red-800 text-xs"
                        >
                          Eliminar
                        </button>
                      </td>
                    )}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {totales && (
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-sm font-semibold text-gray-900 mb-3">Totales por categoría</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <TotalBox label="Humanas dinero" value={totales.humanasDinero} />
            <TotalBox label="Humanas especie" value={totales.humanasEspecie} />
            <TotalBox label="Jurídicas dinero" value={totales.juridicasDinero} />
            <TotalBox label="Jurídicas especie" value={totales.juridicasEspecie} />
          </div>
          <div className="mt-4 flex items-center justify-between border-t border-gray-200 pt-3">
            <div className="text-sm text-gray-600">
              Total aportes privados (se refleja en cuentas 4.2.x del Paso 3)
            </div>
            <div className="text-2xl font-bold font-mono text-green-700">
              $ {formatImporte(totales.total)}
            </div>
          </div>
          {totales.cantidadAlertas > 0 && (
            <div className="mt-3 bg-amber-50 border-l-4 border-amber-400 p-3 text-sm text-amber-900">
              {totales.cantidadAlertas} aportante(s) con alerta de proveedor del Estado.
            </div>
          )}
        </div>
      )}

      <div className="flex justify-between pt-2">
        <button
          type="button"
          onClick={onBack}
          className="bg-white border border-gray-300 text-gray-700 hover:bg-gray-50 text-sm font-medium px-5 py-2 rounded-lg"
        >
          ← Anterior
        </button>
        <button
          type="button"
          onClick={onNext}
          className="bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium px-5 py-2 rounded-lg"
        >
          Siguiente →
        </button>
      </div>

      {/* Modal alta/edición */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900">
                {editando ? 'Editar aporte' : 'Nuevo aporte'}
              </h3>
              <button type="button" onClick={closeModal} className="text-gray-400 hover:text-gray-600">✕</button>
            </div>
            <form onSubmit={handleSubmit((d) => saveMutation.mutate(d))} className="p-6 space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-semibold uppercase text-gray-600 mb-1">Tipo de persona *</label>
                  <select
                    {...register('tipoPersona')}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                  >
                    <option value="HUMANA">Persona humana</option>
                    <option value="JURIDICA">Persona jurídica</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-semibold uppercase text-gray-600 mb-1">Modalidad *</label>
                  <select
                    {...register('modalidad')}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                  >
                    <option value="TRANSFERENCIA">Transferencia</option>
                    <option value="DEPOSITO">Depósito</option>
                    <option value="ESPECIE">En especie</option>
                  </select>
                </div>
              </div>

              {tipoPersonaActual === 'HUMANA' ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-semibold uppercase text-gray-600 mb-1">Apellido *</label>
                    <input {...register('apellido')} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
                    {errors.apellido && <p className="mt-1 text-xs text-red-600">{errors.apellido.message}</p>}
                  </div>
                  <div>
                    <label className="block text-xs font-semibold uppercase text-gray-600 mb-1">Nombre *</label>
                    <input {...register('nombre')} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
                    {errors.nombre && <p className="mt-1 text-xs text-red-600">{errors.nombre.message}</p>}
                  </div>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-semibold uppercase text-gray-600 mb-1">Razón social *</label>
                    <input {...register('razonSocial')} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
                    {errors.razonSocial && <p className="mt-1 text-xs text-red-600">{errors.razonSocial.message}</p>}
                  </div>
                  <div>
                    <label className="block text-xs font-semibold uppercase text-gray-600 mb-1">Denominación / Sigla</label>
                    <input {...register('apellido')} placeholder="Ej: S.R.L." className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
                  </div>
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {tipoPersonaActual === 'HUMANA' && (
                  <div>
                    <label className="block text-xs font-semibold uppercase text-gray-600 mb-1">DNI *</label>
                    <input {...register('dni')} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm font-mono" placeholder="Sin puntos" />
                    {errors.dni && <p className="mt-1 text-xs text-red-600">{errors.dni.message}</p>}
                  </div>
                )}
                <div>
                  <label className="block text-xs font-semibold uppercase text-gray-600 mb-1">CUIT *</label>
                  <input {...register('cuit')} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm font-mono" placeholder="20-12345678-9" />
                  {errors.cuit && <p className="mt-1 text-xs text-red-600">{errors.cuit.message}</p>}
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-semibold uppercase text-gray-600 mb-1">Fecha del aporte *</label>
                  <input type="date" {...register('fecha')} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
                  {errors.fecha && <p className="mt-1 text-xs text-red-600">{errors.fecha.message}</p>}
                </div>
                <div>
                  <label className="block text-xs font-semibold uppercase text-gray-600 mb-1">Importe *</label>
                  <input type="number" step="0.01" {...register('importe')} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm font-mono text-right" />
                  {errors.importe && <p className="mt-1 text-xs text-red-600">{errors.importe.message}</p>}
                </div>
              </div>

              {modalidadActual === 'ESPECIE' && (
                <div>
                  <label className="block text-xs font-semibold uppercase text-gray-600 mb-1">Descripción del aporte en especie *</label>
                  <textarea
                    {...register('descripcionEspecie')}
                    rows={2}
                    placeholder="Ej: Combustible campaña (500 lts)"
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                  />
                  {errors.descripcionEspecie && <p className="mt-1 text-xs text-red-600">{errors.descripcionEspecie.message}</p>}
                </div>
              )}

              <div className="flex justify-end gap-2 pt-2 border-t border-gray-200">
                <button
                  type="button"
                  onClick={closeModal}
                  disabled={saveMutation.isPending}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={saveMutation.isPending}
                  className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg disabled:opacity-50"
                >
                  {saveMutation.isPending ? 'Guardando...' : 'Guardar'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

function TotalBox({ label, value }: { label: string; value: number }) {
  return (
    <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 text-center">
      <div className="text-xs font-semibold uppercase text-gray-600 mb-1">{label}</div>
      <div className="text-lg font-bold font-mono text-gray-900">$ {formatImporte(value)}</div>
    </div>
  );
}
